import { Code, ConnectError, type CallOptions, type Client } from "@connectrpc/connect";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { WebhooksService, type GetEndpointStatsResponse, type ListMessageAttemptsForEndpointResponse } from "#gen/webhooks/v1/service_pb.js";
import type { Endpoint, EndpointCustomHeader, EventType } from "#gen/webhooks/v1/webhook_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { FieldMask } from "@bufbuild/protobuf/wkt";

interface EventTypeList {
	eventTypes: EventType[];
	pageInfo?: PaginationPageInfo;
}

interface EndpointList {
	endpoints: Endpoint[];
	pageInfo?: PaginationPageInfo;
}

export class WebhooksClient {
	private client: Client<typeof WebhooksService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.webhooks || "/", WebhooksService, options);
	}

	async getDashboardUrl(workspaceId: string): Promise<string> {
		try {
			const res = await this.client.getReceiverDashboard({ receiverId: workspaceId });
			return res.dashboardUrl;
		} catch (e) {
			// for now just return an unset url if the receiver has not been set up
			if (e instanceof ConnectError && e.code == Code.NotFound) {
				return "";
			}
			throw handleError(e);
		}
	}

	async listEndpoints(workspaceId: string, paginate?: PaginationArguments, options?: CallOptions): Promise<EndpointList> {
		try {
			const res = await this.client.listEndpointsForReceiver({ receiverId: workspaceId, pagination: paginate }, options);
			return { endpoints: res.endpoints, pageInfo: res.pageInfo };
		} catch (e) {
			if (e instanceof ConnectError && e.code == Code.NotFound) {
				return {
					endpoints: [],
				};
			}
			throw handleError(e);
		}
	}

	async listEventTypes(paginate?: PaginationArguments, options?: CallOptions): Promise<EventTypeList> {
		try {
			const res = await this.client.listEventTypes({ pagination: paginate }, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createEndpoint(workspaceId: string, url: string, filterTypes?: string[], description?: string, rateLimit?: number, options?: CallOptions): Promise<Endpoint | undefined> {
		try {
			const res = await this.client.createEndpoint({ description: description, receiverId: workspaceId, url: url, rateLimit: rateLimit, filterTypes: filterTypes }, options);
			return res?.endpoint;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEndpoint(workspaceId: string, endpointId: string, options?: CallOptions): Promise<Endpoint> {
		try {
			const res = await this.client.getEndpoint({ receiverId: workspaceId, endpointId }, options);
			return res.endpoint!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateEndpoint(endpoint: Endpoint, mask: FieldMask, options?: CallOptions): Promise<Endpoint> {
		try {
			const res = await this.client.updateEndpoint({ endpoint, mask }, options);
			if (res?.endpoint === undefined) {
				throw new Error("Error updating endpoint");
			}
			return res.endpoint;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteEndpoint(workspaceId: string, endpointId: string, options?: CallOptions): Promise<void> {
		try {
			await this.client.deleteEndpoint({ receiverId: workspaceId, endpointId }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEndpointHeaders(workspaceId: string, endpointId: string, options?: CallOptions): Promise<EndpointCustomHeader[]> {
		try {
			const res = await this.client.listEndpointCustomHeaders({ receiverId: workspaceId, endpointId }, options);
			return res.headers;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateEndpointHeaders(workspaceId: string, endpointId: string, headers: Record<string, string>, options?: CallOptions): Promise<void> {
		try {
			await this.client.updateEndpointCustomHeaders({ receiverId: workspaceId, endpointId, headers: headers }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEndpointSecret(workspaceId: string, endpointId: string, options?: CallOptions): Promise<string> {
		try {
			const res = await this.client.getEndpointSecret({ receiverId: workspaceId, endpointId }, options);
			if (res.secret === undefined) {
				throw new Error("Secret not found");
			}

			return res.secret.key;
		} catch (e) {
			throw handleError(e);
		}
	}

	async rotateEndpointSecret(workspaceId: string, endpointId: string, options?: CallOptions): Promise<void> {
		try {
			await this.client.rotateEndpointSecret({ receiverId: workspaceId, endpointId }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMessageAttemptsForEndpoint(workspaceId: string, endpointId: string, pagination?: PaginationArguments, options?: CallOptions): Promise<ListMessageAttemptsForEndpointResponse> {
		try {
			const res = await this.client.listMessageAttemptsForEndpoint({ receiverId: workspaceId, endpointId, pagination }, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEndpointStats(workspaceId: string, endpointId: string, options?: CallOptions): Promise<GetEndpointStatsResponse> {
		try {
			const res = await this.client.getEndpointStats({ receiverId: workspaceId, endpointId }, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
